import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { ProductReadModelSort, SortOrder } from '@client/api';
import { DropDownSort, DropDownSortEntry, SearchField, useSearching, useSorting } from '@client/toolkit';

import { useProductListSearchQueryParams, useProductListSortQueryParams } from '../utils/use-product-list-query-params';

const SORT_OPTIONS: DropDownSortEntry<ProductReadModelSort, SortOrder>[] = [
  { property: 'Name', order: 'Asc', label: 'product.name' },
  { property: 'Name', order: 'Desc', label: 'product.name' },
];

interface ProductListFilterProps {
  totalCount?: number;
}

export const ProductListToolbar = ({ totalCount }: ProductListFilterProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [searchQueryParams, setSearchQueryParam] = useProductListSearchQueryParams();
  const [sortQueryParams, setSortQueryParam] = useProductListSortQueryParams();
  const { search, clearSearch, debouncedSearch, updateSearch } = useSearching({
    search: searchQueryParams.productsSearch,
  });
  const { sortProperty, sortOrder, updateSortOrder, updateSortProperty } = useSorting<ProductReadModelSort, SortOrder>({
    sortProperty: sortQueryParams.productsSortProperty,
    sortOrder: sortQueryParams.productsSortOrder,
  });

  useUpdateEffect(() => {
    setSearchQueryParam({
      productsSearch: debouncedSearch ?? undefined,
    });
  }, [debouncedSearch]);

  useUpdateEffect(() => {
    setSortQueryParam({
      productsSortOrder: sortOrder ?? undefined,
    });
  }, [sortOrder]);

  useUpdateEffect(() => {
    setSortQueryParam({
      productsSortProperty: sortProperty ?? undefined,
    });
  }, [sortProperty]);

  return (
    <div className="flex justify-between items-center px-4 py-4">
      <div className="flex w-6/12 items-center">
        <SearchField value={search} onChange={updateSearch} onClear={clearSearch} placeholder={tCommon('search')} />
        <div className="flex">
          <span className="px-2 whitespace-nowrap text-primary">{`${totalCount} ${tCommon('entries')}`}</span>
        </div>
      </div>
      <DropDownSort<ProductReadModelSort, SortOrder>
        selected={{
          order: sortOrder,
          property: sortProperty,
        }}
        onSortChange={(entry) => {
          updateSortOrder(entry.order);
          updateSortProperty(entry.property);
        }}
        promptLabel={`${tCommon('sortBy')}:`}
        getSelectedLabel={(label) => (label ? tGlobal(label) : '')}
        getOptionLabel={(label) => (label ? tGlobal(label) : '')}
        ascLabel={tCommon('asc')}
        descLabel={tCommon('desc')}
        columns={SORT_OPTIONS}
      />
    </div>
  );
};
